import {
    WidgetContainer,
    WidgetContainerAbsolutePositionConfig,
    WidgetContainerAttachedPositionConfig,
    WidgetContainerRelativePositionConfig,
    widgetSorter
} from './WidgetInterface';

/**
 * Placing a "docked" widget container requires aligning it to the appropriate docking point and arranging it to fit with any other
 * containers docked at that point.  The arranging depends on the "mode" specified with the position value.  If specified as
 * "inline", the container will be arranged inline with other containers that have been marked inline and will hug the edge of the
 * viewport.  If specified as "stack", the container will be stacked on top of the inline containers and any other stacked
 * containers causing it to move further to the center of the viewport.
 *
 * @param wl the layout
 * @param wc the container being placed
 */
const placeDocked = (wl: WidgetLayout, wc: WidgetContainer): boolean => {
    if (wc.position[0] === 'docked') {
        wl[wc.position[1]][wc.position[2] || 'inline'].push(wc);
        return true;
    }
    return false;
};

const placeAttached = (wl: WidgetLayout, wc: WidgetContainer): boolean => {
    if (wc.position[0] === 'attached') {
        wl.attachments[wc.id] = [wc, wc.position[1]];
        return true;
    }
    return false;
};

const placeRelative = (wl: WidgetLayout, wc: WidgetContainer): boolean => {
    if (wc.position[0] === 'relative') {
        wl.relatives[wc.id] = [wc, wc.position[1]];
        return true;
    }
    return false;
};

const placeAbsolute = (wl: WidgetLayout, wc: WidgetContainer): boolean => {
    if (wc.position[0] === 'absolute') {
        wl.absolutes[wc.id] = [wc, wc.position[1]];
        return true;
    }
    return false;
};

const placeUnmanaged = (wl: WidgetLayout, wc: WidgetContainer): boolean => {
    if (wc.position[0] === 'unmanaged') {
        wl.unmanageds[wc.id] = [wc];
        return true;
    }
    return false;
};

const placeReducer = (wl: WidgetLayout, wc: WidgetContainer) => {
    placeDocked(wl, wc) || placeAttached(wl, wc) || placeRelative(wl, wc) || placeAbsolute(wl, wc) || placeUnmanaged(wl, wc);
    return wl;
};


/**
 * Defines a data structure for storing a configured stack of docked WidgetContainer objects.
 */
export interface WidgetLayoutDockingPoint {
    inline: WidgetContainer[];
    stack: WidgetContainer[];
}

const makeDockingPoint = () => ({ inline: [], stack: [] }) as WidgetLayoutDockingPoint;

/**
 * Defines a data structure for storing a configured set of attached WidgetContainer objects.
 */
export type WidgetLayoutAttachmentPoint = WidgetContainer[];

/**
 * Defines a data structure for storing a configured set of absolute WidgetContainer objects.
 */
export type WidgetLayoutAbsolutePoint = WidgetContainer[];

/**
 * Represents the layout of a composition of WidgetContainer objects within the widget layer.
 */
export class WidgetLayout {

    public static default = new WidgetLayout();

    /**
     * Computes the layout of the specified composition of WidgetContainer objects.
     *
     * @param composition The widget composition
     */
    public static compute(composition: Set<WidgetContainer>): WidgetLayout {
        return Array.from(composition)
            .sort(widgetSorter)
            .reduce(placeReducer, new WidgetLayout());
    }

    private constructor() {
    }

    public readonly attachments = {} as Record<string, [WidgetContainer, WidgetContainerAttachedPositionConfig]>;
    public readonly relatives = {} as Record<string, [WidgetContainer, WidgetContainerRelativePositionConfig]>;
    public readonly absolutes = {} as Record<string, [WidgetContainer, WidgetContainerAbsolutePositionConfig]>;
    public readonly unmanageds = {} as Record<string, [WidgetContainer]>;
    public readonly topLeft = makeDockingPoint();
    public readonly top = makeDockingPoint();
    public readonly topRight = makeDockingPoint();
    public readonly right = makeDockingPoint();
    public readonly bottomRight = makeDockingPoint();
    public readonly bottom = makeDockingPoint();
    public readonly bottomLeft = makeDockingPoint();
    public readonly left = makeDockingPoint();
}
